import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ListTodo,
  Plus,
  X,
  Circle,
  Clock,
  AlertTriangle,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Eye,
  EyeOff,
  GripVertical,
  Layers,
  ArrowUpToLine,
  ArrowDownToLine,
  ArrowRight,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiGet, apiPost, apiPatch } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import type { DragHandleProps } from './DragDropList';

interface Task {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  meeting_instance_id?: string;
  meeting_date?: string;
}

interface MeetingTasksSidebarProps {
  instanceId: string;
  seriesId?: string;
  meetingName?: string;
  refreshTrigger?: number;
  onTaskStatusToggled?: (taskId: string, newStatus: string) => void;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  todo: <Circle size={12} className="text-text-muted" />,
  next: <ArrowRight size={12} className="text-sky-400" />,
  in_progress: <Clock size={12} className="text-accent" />,
  blocked: <AlertTriangle size={12} className="text-danger" />,
  done: <CheckSquare size={12} className="text-success" />,
  cancelled: <X size={12} className="text-text-muted" />,
};

const PRIORITY_DOT_COLORS: Record<string, string> = {
  urgent: 'bg-danger',
  high: 'bg-warning',
  medium: 'bg-text-secondary',
  low: 'bg-text-muted',
};

export function MeetingTasksSidebar({ instanceId, seriesId, meetingName, refreshTrigger, onTaskStatusToggled }: MeetingTasksSidebarProps) {
  const toast = useToast();
  const taskLinkState = meetingName ? { from: `/meetings/${instanceId}`, fromLabel: meetingName } : undefined;
  const [carryForwardTasks, setCarryForwardTasks] = useState<Task[]>([]);
  const [meetingTasks, setMeetingTasks] = useState<Task[]>([]);
  const [seriesTasks, setSeriesTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [carryForwardOpen, setCarryForwardOpen] = useState(true);
  const [seriesOpen, setSeriesOpen] = useState(true);
  const [currentOpen, setCurrentOpen] = useState(true);

  // New task form
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const newTaskInputRef = useRef<HTMLInputElement>(null);

  const loadTasks = useCallback(async () => {
    setLoadingTasks(true);
    try {
      const meetingPromise = apiGet<Task[]>(`/tasks?source_meeting_id=${instanceId}&exclude_follow_ups=1`);

      if (seriesId) {
        const [meeting, carryForward, series] = await Promise.all([
          meetingPromise,
          apiGet<Task[]>(`/meeting-series/${seriesId}/carry-forward-tasks?exclude_instance=${instanceId}`),
          apiGet<Task[]>(`/meeting-series/${seriesId}/series-tasks`),
        ]);
        setMeetingTasks(meeting);
        setCarryForwardTasks(carryForward);

        // Deduplicate: remove series tasks that already appear in meeting or carry-forward
        const shownIds = new Set([...meeting.map(t => t.id), ...carryForward.map(t => t.id)]);
        setSeriesTasks(series.filter(t => !shownIds.has(t.id)));
      } else {
        const meeting = await meetingPromise;
        setMeetingTasks(meeting);
        setCarryForwardTasks([]);
        setSeriesTasks([]);
      }
    } catch (err) {
      console.error('Failed to load tasks:', err);
    } finally {
      setLoadingTasks(false);
    }
  }, [instanceId, seriesId]);

  useEffect(() => { loadTasks(); }, [loadTasks, refreshTrigger]);

  async function handleCreateTask() {
    const trimmed = newTaskTitle.trim();
    if (!trimmed || savingTask) return;
    setSavingTask(true);
    try {
      await apiPost('/tasks', {
        title: trimmed,
        source_meeting_id: instanceId,
        priority: 'medium',
      });
      setNewTaskTitle('');
      setShowNewTask(false);
      toast.success('Task created');
      loadTasks();
    } catch (err) {
      console.error('Failed to create task:', err);
      toast.error('Failed to create task');
    } finally {
      setSavingTask(false);
    }
  }

  async function handleToggleStatus(taskId: string, currentStatus: string) {
    const newStatus = currentStatus === 'done' ? 'todo' : 'done';
    try {
      await apiPatch(`/tasks/${taskId}`, { status: newStatus });
      onTaskStatusToggled?.(taskId, newStatus);
      loadTasks();
    } catch (err) {
      console.error('Failed to update task status:', err);
      toast.error('Failed to update task');
    }
  }

  async function handleMoveToSeries(taskId: string) {
    if (!seriesId) return;
    try {
      await apiPost(`/tasks/${taskId}/move`, { target: { type: 'meeting_series', id: seriesId } });
      toast.success('Moved to series');
      loadTasks();
    } catch (err) {
      console.error('Failed to move task:', err);
      toast.error('Failed to move task');
    }
  }

  async function handleMoveToInstance(taskId: string) {
    try {
      await apiPost(`/tasks/${taskId}/move`, { target: { type: 'meeting_instance', id: instanceId } });
      toast.success('Moved to this meeting');
      loadTasks();
    } catch (err) {
      console.error('Failed to move task:', err);
      toast.error('Failed to move task');
    }
  }

  useEffect(() => {
    if (showNewTask && newTaskInputRef.current) {
      newTaskInputRef.current.focus();
    }
  }, [showNewTask]);

  const visibleMeetingTasks = showCompleted
    ? meetingTasks
    : meetingTasks.filter(t => t.status !== 'done' && t.status !== 'cancelled');

  const visibleSeriesTasks = showCompleted
    ? seriesTasks
    : seriesTasks.filter(t => t.status !== 'done' && t.status !== 'cancelled');

  const allTasks = [...meetingTasks, ...seriesTasks];
  const completedCount = allTasks.filter(t => t.status === 'done' || t.status === 'cancelled').length;

  // --- Cross-container drag-and-drop ---
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function findContainer(taskId: string): 'series' | 'meeting' | null {
    if (visibleSeriesTasks.some(t => t.id === taskId)) return 'series';
    if (visibleMeetingTasks.some(t => t.id === taskId)) return 'meeting';
    return null;
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveDragId(String(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    const sourceContainer = findContainer(activeId);

    // Determine target container: if dropped on a droppable zone (section header), use that; otherwise, use the container of the over item
    let targetContainer: 'series' | 'meeting' | null = null;
    if (overId === 'droppable-series') {
      targetContainer = 'series';
    } else if (overId === 'droppable-meeting') {
      targetContainer = 'meeting';
    } else {
      targetContainer = findContainer(overId);
    }

    if (!sourceContainer || !targetContainer) return;

    // Cross-container move
    if (sourceContainer !== targetContainer) {
      if (targetContainer === 'meeting') {
        handleMoveToInstance(activeId);
      } else if (targetContainer === 'series' && seriesId) {
        handleMoveToSeries(activeId);
      }
      return;
    }

    // Same container reorder (only for meeting tasks)
    if (sourceContainer === 'meeting' && activeId !== overId) {
      const oldIndex = visibleMeetingTasks.findIndex(t => t.id === activeId);
      const newIndex = visibleMeetingTasks.findIndex(t => t.id === overId);
      if (oldIndex === -1 || newIndex === -1) return;
      const newOrder = arrayMove(visibleMeetingTasks, oldIndex, newIndex);
      setMeetingTasks(prev => {
        const completed = prev.filter(t => !newOrder.find(n => n.id === t.id));
        return [...newOrder, ...completed];
      });
      const ids = newOrder.map(t => t.id);
      apiPatch('/tasks/reorder', { task_ids: ids }).catch(console.error);
    }
  }

  if (loadingTasks) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="animate-spin rounded-full h-4 w-4 border border-accent border-t-transparent" />
      </div>
    );
  }

  const hasDraggableSections = seriesId && (visibleSeriesTasks.length > 0 || visibleMeetingTasks.length > 0);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border">
        <ListTodo size={14} className="text-text-muted" />
        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide flex-1">Tasks</span>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3">
        {/* Carry-forward tasks — not draggable */}
        {carryForwardTasks.length > 0 && (
          <div>
            <button
              onClick={() => setCarryForwardOpen(!carryForwardOpen)}
              className="flex items-center gap-1.5 w-full text-left py-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider hover:text-text-secondary transition-colors"
            >
              {carryForwardOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
              <RotateCcw size={10} />
              Carry Forward
              <span className="text-[9px] font-normal bg-bg-hover rounded-full px-1.5 py-0.5 ml-auto">
                {carryForwardTasks.length}
              </span>
            </button>
            {carryForwardOpen && (
              <div className="space-y-0.5 mt-1">
                {carryForwardTasks.map(task => (
                  <TaskRow key={task.id} task={task} showMeetingDate onToggleStatus={handleToggleStatus} linkState={taskLinkState} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Wrap series + meeting sections in a single DndContext for cross-container drag */}
        {hasDraggableSections ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {/* Series-linked tasks */}
            {seriesTasks.length > 0 && (
              <SeriesDropZone isOver={activeDragId !== null && findContainer(activeDragId) === 'meeting'}>
                <button
                  onClick={() => setSeriesOpen(!seriesOpen)}
                  className="flex items-center gap-1.5 w-full text-left py-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider hover:text-text-secondary transition-colors"
                >
                  {seriesOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                  <Layers size={10} />
                  Series Tasks
                  <span className="text-[9px] font-normal bg-bg-hover rounded-full px-1.5 py-0.5 ml-auto">
                    {seriesTasks.length}
                  </span>
                </button>
                {seriesOpen && (
                  <SortableContext items={visibleSeriesTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-0.5 mt-1">
                      {visibleSeriesTasks.length === 0 && (
                        <p className="text-xs text-text-muted py-1 px-1">All tasks completed</p>
                      )}
                      {visibleSeriesTasks.map(task => (
                        <SortableTaskRow key={task.id} task={task} onToggleStatus={handleToggleStatus} onMoveToInstance={() => handleMoveToInstance(task.id)} linkState={taskLinkState} />
                      ))}
                    </div>
                  </SortableContext>
                )}
              </SeriesDropZone>
            )}

            {/* Current meeting tasks */}
            <MeetingDropZone isOver={activeDragId !== null && findContainer(activeDragId) === 'series'}>
              <button
                onClick={() => setCurrentOpen(!currentOpen)}
                className="flex items-center gap-1.5 w-full text-left py-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider hover:text-text-secondary transition-colors"
              >
                {currentOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                This Meeting
                <span className="text-[9px] font-normal bg-bg-hover rounded-full px-1.5 py-0.5 ml-auto">
                  {meetingTasks.length}
                </span>
              </button>
              {currentOpen && (
                <SortableContext items={visibleMeetingTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-0.5 mt-1">
                    {visibleMeetingTasks.length === 0 && meetingTasks.length === 0 && (
                      <p className="text-xs text-text-muted py-1 px-1">No tasks yet</p>
                    )}
                    {visibleMeetingTasks.length === 0 && meetingTasks.length > 0 && (
                      <p className="text-xs text-text-muted py-1 px-1">All tasks completed</p>
                    )}
                    {visibleMeetingTasks.map(task => (
                      <SortableTaskRow key={task.id} task={task} onToggleStatus={handleToggleStatus} onMoveToSeries={() => handleMoveToSeries(task.id)} linkState={taskLinkState} />
                    ))}
                  </div>
                </SortableContext>
              )}
            </MeetingDropZone>
          </DndContext>
        ) : (
          <>
            {/* Series tasks — no drag context needed (no seriesId or empty) */}
            {seriesTasks.length > 0 && (
              <div>
                <button
                  onClick={() => setSeriesOpen(!seriesOpen)}
                  className="flex items-center gap-1.5 w-full text-left py-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider hover:text-text-secondary transition-colors"
                >
                  {seriesOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                  <Layers size={10} />
                  Series Tasks
                  <span className="text-[9px] font-normal bg-bg-hover rounded-full px-1.5 py-0.5 ml-auto">
                    {seriesTasks.length}
                  </span>
                </button>
                {seriesOpen && (
                  <div className="space-y-0.5 mt-1">
                    {visibleSeriesTasks.length === 0 && (
                      <p className="text-xs text-text-muted py-1 px-1">All tasks completed</p>
                    )}
                    {visibleSeriesTasks.map(task => (
                      <TaskRow key={task.id} task={task} onToggleStatus={handleToggleStatus} linkState={taskLinkState} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Current meeting tasks — standalone (no seriesId) */}
            <div>
              <button
                onClick={() => setCurrentOpen(!currentOpen)}
                className="flex items-center gap-1.5 w-full text-left py-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider hover:text-text-secondary transition-colors"
              >
                {currentOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                This Meeting
                <span className="text-[9px] font-normal bg-bg-hover rounded-full px-1.5 py-0.5 ml-auto">
                  {meetingTasks.length}
                </span>
              </button>
              {currentOpen && (
                <div className="space-y-0.5 mt-1">
                  {visibleMeetingTasks.length === 0 && meetingTasks.length === 0 && (
                    <p className="text-xs text-text-muted py-1 px-1">No tasks yet</p>
                  )}
                  {visibleMeetingTasks.length === 0 && meetingTasks.length > 0 && (
                    <p className="text-xs text-text-muted py-1 px-1">All tasks completed</p>
                  )}
                  {visibleMeetingTasks.map(task => (
                    <TaskRow key={task.id} task={task} onToggleStatus={handleToggleStatus} linkState={taskLinkState} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Show/hide completed toggle */}
        {completedCount > 0 && (
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className="flex items-center gap-1 text-[10px] text-text-muted hover:text-text-secondary transition-colors py-1 px-1"
          >
            {showCompleted ? <EyeOff size={10} /> : <Eye size={10} />}
            {showCompleted ? 'Hide' : 'Show'} {completedCount} completed
          </button>
        )}

        {/* New Task inline form */}
        {showNewTask ? (
          <div className="pt-1 px-1">
            <input
              ref={newTaskInputRef}
              type="text"
              value={newTaskTitle}
              onChange={e => setNewTaskTitle(e.target.value)}
              placeholder="Task title..."
              disabled={savingTask}
              className="w-full px-2 py-1.5 bg-bg-input border border-border rounded text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent disabled:opacity-50"
              onKeyDown={e => {
                if (e.key === 'Enter') handleCreateTask();
                if (e.key === 'Escape') {
                  setShowNewTask(false);
                  setNewTaskTitle('');
                }
              }}
            />
            <div className="flex items-center gap-1.5 mt-1.5">
              <button
                onClick={handleCreateTask}
                disabled={!newTaskTitle.trim() || savingTask}
                className="px-2 py-1 text-[10px] bg-accent text-white rounded hover:bg-accent-hover disabled:opacity-50 transition-colors"
              >
                {savingTask ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() => { setShowNewTask(false); setNewTaskTitle(''); }}
                className="px-2 py-1 text-[10px] text-text-muted hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowNewTask(true)}
            className="flex items-center gap-1.5 text-xs text-text-muted hover:text-accent transition-colors py-1 px-1"
          >
            <Plus size={12} />
            New Task
          </button>
        )}
      </div>
    </div>
  );
}

/** Droppable zone wrapper for the Series Tasks section */
function SeriesDropZone({ children, isOver }: { children: React.ReactNode; isOver: boolean }) {
  const { setNodeRef, isOver: isDragOver } = useDroppable({ id: 'droppable-series' });
  const highlight = isOver || isDragOver;
  return (
    <div ref={setNodeRef} className={`rounded-md transition-colors ${highlight ? 'bg-accent/5 ring-1 ring-accent/20' : ''}`}>
      {children}
    </div>
  );
}

/** Droppable zone wrapper for the This Meeting section */
function MeetingDropZone({ children, isOver }: { children: React.ReactNode; isOver: boolean }) {
  const { setNodeRef, isOver: isDragOver } = useDroppable({ id: 'droppable-meeting' });
  const highlight = isOver || isDragOver;
  return (
    <div ref={setNodeRef} className={`rounded-md transition-colors ${highlight ? 'bg-accent/5 ring-1 ring-accent/20' : ''}`}>
      {children}
    </div>
  );
}

/** Sortable wrapper for task rows in the cross-container DndContext */
function SortableTaskRow({ task, onToggleStatus, onMoveToSeries, onMoveToInstance, linkState }: {
  task: Task;
  onToggleStatus?: (id: string, currentStatus: string) => void;
  onMoveToSeries?: () => void;
  onMoveToInstance?: () => void;
  linkState?: { from: string; fromLabel: string };
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 10 : ('auto' as any),
  };

  return (
    <div ref={setNodeRef} style={style}>
      <TaskRow
        task={task}
        dragHandleProps={{ ref: setActivatorNodeRef, listeners, attributes }}
        onToggleStatus={onToggleStatus}
        onMoveToSeries={onMoveToSeries}
        onMoveToInstance={onMoveToInstance}
        linkState={linkState}
      />
    </div>
  );
}

function TaskRow({ task, showMeetingDate, dragHandleProps, onToggleStatus, onMoveToSeries, onMoveToInstance, linkState }: {
  task: Task;
  showMeetingDate?: boolean;
  dragHandleProps?: DragHandleProps;
  onToggleStatus?: (id: string, currentStatus: string) => void;
  onMoveToSeries?: () => void;
  onMoveToInstance?: () => void;
  linkState?: { from: string; fromLabel: string };
}) {
  return (
    <div className="flex items-center gap-1 group">
      {dragHandleProps && (
        <button
          ref={dragHandleProps.ref}
          {...dragHandleProps.listeners}
          {...dragHandleProps.attributes}
          className="cursor-grab active:cursor-grabbing p-0.5 text-text-muted hover:text-text-secondary transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
        >
          <GripVertical size={10} />
        </button>
      )}
      <button
        onClick={() => onToggleStatus?.(task.id, task.status)}
        className="flex-shrink-0 py-1.5 px-1 hover:scale-125 transition-transform"
        title={task.status === 'done' ? 'Mark as todo' : 'Mark as done'}
      >
        {STATUS_ICONS[task.status] ?? <Circle size={12} />}
      </button>
      <Link
        to={`/tasks/${task.id}`}
        state={linkState}
        className="flex items-center gap-2 py-1.5 pr-1 rounded hover:bg-bg-hover transition-colors flex-1 min-w-0 group/link"
      >
        <div className="flex-1 min-w-0">
          <span
            className={`text-xs block truncate ${
              task.status === 'done'
                ? 'line-through text-text-muted'
                : 'text-text-primary group-hover/link:text-accent'
            } transition-colors`}
          >
            {task.title}
          </span>
          {showMeetingDate && task.meeting_date && (
            <span className="text-[10px] text-text-muted block">
              from {new Date(task.meeting_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
        <span
          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
            PRIORITY_DOT_COLORS[task.priority] ?? 'bg-text-muted'
          }`}
          title={task.priority}
        />
      </Link>
      {onMoveToSeries && (
        <button
          onClick={onMoveToSeries}
          className="flex-shrink-0 p-1 text-text-muted hover:text-accent transition-colors opacity-0 group-hover:opacity-100"
          title="Move to series tasks"
        >
          <ArrowUpToLine size={12} />
        </button>
      )}
      {onMoveToInstance && (
        <button
          onClick={onMoveToInstance}
          className="flex-shrink-0 p-1 text-text-muted hover:text-accent transition-colors opacity-0 group-hover:opacity-100"
          title="Move to this meeting"
        >
          <ArrowDownToLine size={12} />
        </button>
      )}
    </div>
  );
}
